import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { COMPANY, FALLBACK_MENU } from '../data/seedData';
import { useCmsMenu } from '../hooks/useCms';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  const location = useLocation();
  const { menuItems } = useCmsMenu();

  // Handle scroll for header background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
    setMobileSubmenu(null);
  }, [location.pathname]);

  // Construct structured menu
  const menu = (() => {
    if (!menuItems || menuItems.length === 0) return FALLBACK_MENU;
    const topLevel = menuItems.filter((i) => !i.parent);
    return topLevel.map((parent) => {
      const parentHrefSlug = (parent.href || '').replace(/^\//, '').split('/')[0].split('#')[0];
      const rawSlug = String(parent.slug?._ || parent.slug || '');
      const cleanSlug = rawSlug.replace(/^menu-/, '');
      const parentTitle = (parent.title || '').trim().toLowerCase();

      const children = menuItems.filter((c) => {
        if (!c.parent) return false;
        const cp = c.parent.trim().toLowerCase();
        return (
          cp === parentHrefSlug.toLowerCase() ||
          cp === cleanSlug.toLowerCase() ||
          cp === rawSlug.toLowerCase() ||
          cp === parentTitle ||
          (parent.title && c.parent === parent.title)
        );
      });
      return {
        title: parent.title,
        href: parent.href,
        children: children.map((c) => ({
          title: c.title,
          href: c.href,
        })),
      };
    });
  })();

  const isLinkActive = (href, children = []) => {
    if (location.pathname === href) return true;
    if (children && children.some((c) => location.pathname === c.href)) return true;
    return false;
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          scrolled
            ? 'bg-slate-900/95 backdrop-blur-md shadow-lg border-b border-slate-800'
            : 'bg-slate-900/90 backdrop-blur-xs border-b border-slate-800/60'
        }`}
      >
        {/* Top Info Bar */}
        <div className="bg-slate-950 text-slate-300 text-xs border-b border-slate-800/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <span className="inline-flex items-center gap-1.5 text-slate-300">
                <i className="fa-solid fa-industry text-[#1e9457]"></i>
                <span className="hidden sm:inline">Specialist i glasfiber og komposit</span>
                <span className="sm:hidden">Specialist i glasfiber</span>
              </span>
              <span className="hidden md:inline-flex items-center gap-1.5 text-slate-400">
                <i className="fa-solid fa-location-dot text-[#1e9457]"></i>
                Jernvej 6, Mønsted, 8800 Viborg
              </span>
            </div>

            <div className="flex items-center gap-6">
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="inline-flex items-center gap-2 text-slate-200 hover:text-white font-medium transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-[#1e9457]"></i>
                <span className="whitespace-nowrap">{COMPANY.phone}</span>
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                className="hidden sm:inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope text-[#1e9457]"></i>
                <span className="whitespace-nowrap">{COMPANY.email}</span>
              </a>
            </div>
          </div>
        </div>

        {/* Main Navigation Bar */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group focus:outline-none">
              <img
                src={COMPANY.logo}
                alt="RB Glasfiber A/S Logo"
                className="h-11 sm:h-12 w-auto object-contain transition-transform group-hover:scale-105 duration-200"
              />
            </Link>

            {/* Desktop Navigation (>= 1200px / xl breakpoint) */}
            <nav className="hidden xl:flex items-center gap-1">
              {menu.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const active = isLinkActive(item.href, item.children);

                if (hasChildren) {
                  return (
                    <div
                      key={item.title}
                      className="relative"
                      onMouseEnter={() => setOpenDropdown(item.title)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <NavLink
                        to={item.href}
                        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-semibold tracking-wide transition-all ${
                          active
                            ? 'text-white bg-slate-800 border-b-2 border-[#1e9457]'
                            : 'text-slate-200 hover:text-white hover:bg-slate-800/60'
                        }`}
                      >
                        {item.title}
                        <i
                          className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                            openDropdown === item.title ? 'rotate-180 text-[#1e9457]' : 'text-slate-400'
                          }`}
                        ></i>
                      </NavLink>

                      {/* Dropdown Panel */}
                      {openDropdown === item.title && (
                        <div className="absolute top-full left-0 w-64 pt-2 z-50">
                          <div className="bg-slate-900 border border-slate-800 rounded-lg shadow-2xl p-2.5 space-y-1">
                            {item.children.map((child) => (
                              <Link
                                key={child.title}
                                to={child.href}
                                onClick={() => setOpenDropdown(null)}
                                className={`block px-3 py-2 rounded text-sm transition-colors ${
                                  location.pathname === child.href
                                    ? 'bg-slate-800 text-white font-medium border-l-2 border-[#1e9457]'
                                    : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
                                }`}
                              >
                                {child.title}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={item.title}
                    to={item.href}
                    className={`px-3.5 py-2 rounded-md text-sm font-semibold tracking-wide transition-all ${
                      active
                        ? 'text-white bg-slate-800 border-b-2 border-[#1e9457]'
                        : 'text-slate-200 hover:text-white hover:bg-slate-800/60'
                    }`}
                  >
                    {item.title}
                  </NavLink>
                );
              })}
            </nav>

            {/* Right CTAs */}
            <div className="hidden xl:flex items-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded bg-[#1e9457] hover:bg-[#15703f] text-white text-sm font-bold tracking-wide uppercase shadow-sm transition-all duration-200 hover:shadow-md cursor-pointer"
              >
                <span>Få en løsning</span>
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>
            </div>

            {/* Mobile / Tablet Burger Trigger (< 1200px / below xl) */}
            <div className="flex xl:hidden items-center gap-3">
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-slate-800 text-[#1e9457] hover:bg-slate-700 transition-colors"
                aria-label="Ring til os"
              >
                <i className="fa-solid fa-phone"></i>
              </a>

              <button
                type="button"
                onClick={() => setMobileOpen(!mobileOpen)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-slate-800 text-white hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-[#1e9457]"
                aria-expanded={mobileOpen}
                aria-label="Åbn menu"
              >
                <i className={`fa-solid ${mobileOpen ? 'fa-xmark' : 'fa-bars'} text-lg`}></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Navigation (< 1200px) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 xl:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-950/80 backdrop-blur-xs"
            onClick={() => setMobileOpen(false)}
          ></div>

          {/* Drawer */}
          <div className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-slate-900 border-l border-slate-800 shadow-2xl flex flex-col z-50 animate-slideInRight">
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-800">
              <Link to="/" onClick={() => setMobileOpen(false)}>
                <img
                  src={COMPANY.logo}
                  alt="RB Glasfiber A/S Logo"
                  className="h-9 w-auto object-contain"
                />
              </Link>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="w-10 h-10 rounded-md bg-slate-800 text-slate-300 hover:text-white flex items-center justify-center focus:outline-none"
                aria-label="Luk menu"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>

            {/* Drawer Links */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-2">
              {menu.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isSubmenuOpen = mobileSubmenu === item.title;
                const active = isLinkActive(item.href, item.children);

                return (
                  <div key={item.title} className="border-b border-slate-800/60 pb-2">
                    <div className="flex items-center justify-between">
                      <Link
                        to={item.href}
                        onClick={() => setMobileOpen(false)}
                        className={`text-base font-semibold py-2 transition-colors ${
                          active ? 'text-[#1e9457]' : 'text-slate-100 hover:text-white'
                        }`}
                      >
                        {item.title}
                      </Link>

                      {hasChildren && (
                        <button
                          type="button"
                          onClick={() => setMobileSubmenu(isSubmenuOpen ? null : item.title)}
                          className="p-2 text-slate-400 hover:text-white focus:outline-none"
                          aria-label={`Vis underpunkter for ${item.title}`}
                        >
                          <i
                            className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                              isSubmenuOpen ? 'rotate-180 text-[#1e9457]' : ''
                            }`}
                          ></i>
                        </button>
                      )}
                    </div>

                    {/* Submenu */}
                    {hasChildren && isSubmenuOpen && (
                      <div className="pl-4 py-2 space-y-1.5 bg-slate-950/40 rounded mt-1 border-l-2 border-[#1e9457]">
                        {item.children.map((child) => (
                          <Link
                            key={child.title}
                            to={child.href}
                            onClick={() => setMobileOpen(false)}
                            className={`block py-1.5 text-sm transition-colors ${
                              location.pathname === child.href
                                ? 'text-[#1e9457] font-medium'
                                : 'text-slate-300 hover:text-white'
                            }`}
                          >
                            {child.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Drawer Footer Contact */}
            <div className="p-6 bg-slate-950 border-t border-slate-800 space-y-4">
              <Link
                to="/kontakt"
                onClick={() => setMobileOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded bg-[#1e9457] hover:bg-[#15703f] text-white font-bold uppercase tracking-wider text-sm shadow transition-colors"
              >
                <span>Kontakt os</span>
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>

              <div className="space-y-2 text-xs text-slate-300">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#1e9457]"></i>
                  <a href={`tel:${COMPANY.phoneRaw}`} className="font-semibold text-white whitespace-nowrap">
                    {COMPANY.phone}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-envelope text-[#1e9457]"></i>
                  <a href={`mailto:${COMPANY.email}`} className="text-slate-300 hover:text-white whitespace-nowrap">
                    {COMPANY.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
